import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { _GetSelectedProduct } from 'autogen/swagger/all';
import { BackInStockNotification } from 'autogen/translation-keys/trans-website-shared-buttons';
import { Button, ButtonWithIcon } from 'components/Button';
import useFindationQuickView from 'components/Findation/hooks/useFindationQuickView';
import Icon from 'components/Icon';
import NotificationForm from 'components/product/notification';
import useNotificationFormQuickView from 'components/product/notification/shared/hooks/useNotificationFormQuickView';
import { useSlides } from 'components/quickView';
import isClient from 'helpers/isClient';
import { eventCategoryToContext } from 'modules/tracking/GA4';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import type { IProductActionTrackingContext, ProductActionProps } from '../../shared/types';

function track(trackingInfo: ProductActionProps['BackInStockNotification']['trackingInfo']) {
    if (!isClient) return;

    const { eventCategory } = trackingInfo;

    const context = eventCategoryToContext(eventCategory);

    GA4Tracking({
        eventName: 'notification_request',
        context,
        category: 'ecommerce',
        eventParams: [{ param_name: 'notification_type', param_value: 'back_in_stock' }]
    });
}

export function useOpenStockNotificationCallback({
    productId,
    trackingInfo
}: {
    productId: number;
    trackingInfo: IProductActionTrackingContext;
}) {
    const [, toggle] = useNotificationFormQuickView();
    const [{ isOpen: findationQuickViewIsOpen }] = useFindationQuickView();
    const { push } = useSlides();

    const onClickHandler = useCallback(async () => {
        await _GetSelectedProduct({ productId }).then((data) => {
            if (findationQuickViewIsOpen) {
                push(() => data && <NotificationForm model={{ ...data, isPreorder: false }} />);
            } else {
                toggle({
                    isOpen: true,
                    notificationModel: { ...data, isPreorder: false }
                });
            }
        });

        track(trackingInfo);
    }, [productId, toggle, trackingInfo, push, findationQuickViewIsOpen]);

    return onClickHandler;
}

export function StockNotificationButton({
    params,
    trackingInfo,
    children,
    ...restProps
}: ProductActionProps['BackInStockNotification']) {
    const { t } = useTranslation();
    const { productId } = params;
    const onClickHandler = useOpenStockNotificationCallback({ productId, trackingInfo });

    return (
        <Button onClick={onClickHandler} variant="primary" type="button" {...restProps}>
            {children || t(BackInStockNotification)}
        </Button>
    );
}

export function StockNotificationButtonCompact({
    params,
    trackingInfo,
    ...restProps
}: ProductActionProps['BackInStockNotification']) {
    const { productId } = params;
    const onClickHandler = useOpenStockNotificationCallback({ productId, trackingInfo });
    const { t } = useTranslation();

    return (
        <ButtonWithIcon
            icon={<Icon type="Bell" size={16} />}
            onClick={onClickHandler}
            variant="secondary"
            type="button"
            aria-label={t(BackInStockNotification)}
            {...restProps}
        />
    );
}

export default Object.assign(StockNotificationButton, {
    Compact: StockNotificationButtonCompact
});
